import { useMutation, useQuery } from 'react-apollo';

import { PhotoForCommentsAndReactions } from '../../../graphql/queries/photos';
import { MeQuery } from '../../../graphql/queries/account';
import getSystemStickers from '../../../services/cards/graphql/queries/getSystemStickers';
import { createLoadMoreMethod } from '../../../util/apollo';
import {
  AccountFileReactionCreate,
  AccountFileReactionDelete,
} from '../../../graphql/mutations/reactions';
import uuidv4 from '../../../util/uuid';
import { VideoForCommentsAndReactions } from '../../../graphql/queries/video';
import useAuthenticated from '../../../hooks/useAuthenticated';

const useFileReactionPicker = ({ photoId, videoId, isProEntity }) => {
  const authenticated = useAuthenticated();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const {
    data: { photo } = {},
    loading: loadingPhoto,
    error: photoError,
    refetch: photoRefetch,
  } = useQuery(PhotoForCommentsAndReactions, {
    variables: {
      id: photoId,
      root_only: true,
    },
    skip: !photoId || !authenticated || isProEntity,
  });
  const {
    data: { video } = {},
    loading: loadingVideo,
    error: videoError,
    refetch: videoRefetch,
  } = useQuery(VideoForCommentsAndReactions, {
    variables: {
      id: videoId,
      root_only: true,
    },
    skip: !videoId || !authenticated || isProEntity,
  });

  const {
    data: { systemStickers } = {},
    loading: loadingStickers,
    fetchMore,
  } = useQuery(getSystemStickers, {
    variables: {
      system_sticker_group: 'ACCOUNT_FILE_REACTION',
    },
  });

  const [accountFileReactionCreate] = useMutation(AccountFileReactionCreate);
  const [accountFileReactionDelete] = useMutation(AccountFileReactionDelete);
  const entity = photo || video;

  const loadMore = createLoadMoreMethod({
    source: systemStickers,
    recordsPath: '',
    fetchMore,
    limit: 150,
  });

  const onReactionCreate = async systemSticker => {
    const accountFileReaction = {
      id: uuidv4(),
      system_sticker: {
        ...systemSticker,
      },
      photo: photoId
        ? {
            id: photoId,
            __typename: 'Photo',
          }
        : null,
      video: videoId
        ? {
            id: videoId,
            __typename: 'Video',
          }
        : null,
      __typename: 'AccountFileReaction',
    };
    await accountFileReactionCreate({
      variables: {
        input: {
          photo_id: photoId,
          video_id: videoId,
          system_sticker_id: systemSticker.id,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        accountFileReactionCreate: {
          __typename: 'AccountFileReactionCreatePayload',
          account_file_reaction: {
            id: accountFileReaction.id,
            photo: photoId
              ? {
                  id: photoId,
                  account_file_reaction: accountFileReaction,
                  __typename: 'Album',
                }
              : null,
            video: videoId
              ? {
                  id: videoId,
                  account_file_reaction: accountFileReaction,
                  __typename: 'Video',
                }
              : null,
            __typename: 'AccountFileReaction',
          },
        },
      },
    });

    await (photoId ? photoRefetch : videoRefetch)();
  };

  const onReactionDelete = async systemStickerId => {
    await accountFileReactionDelete({
      variables: {
        input: {
          photo_id: photoId,
          video_id: videoId,
          system_sticker_id: systemStickerId,
        },
      },
    });

    await (photoId ? photoRefetch : videoRefetch)();
  };

  return {
    user,
    entity,
    error: photoError || videoError,
    loadMore,
    systemStickers,
    loading: loadingStickers || loadingPhoto || loadingVideo,
    onReactionCreate,
    onReactionDelete,
    accountFileReaction: entity?.account_file_reaction,
  };
};

export default useFileReactionPicker;
